// Bildirim Merkezi — Tur 2.5C
// Top bar'daki zil ikonuna tıklayınca açılan popover.
// Server-side pagination, unread badge, tekil ve toplu okundu.
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Bell, BellOff, Check, CheckCheck, Loader2, MessageSquare, Users } from "lucide-react";
import { useUnread } from "@/context/UnreadContext";

function relativeTime(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then) / 1000;
    if (diff < 60) return "az önce";
    if (diff < 3600) return `${Math.floor(diff / 60)} dk önce`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} sa önce`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} gün önce`;
    return new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch { return ""; }
}

function NotificationIcon({ kind }) {
  if (kind === "general_message") return <Users className="h-4 w-4 text-primary" />;
  if (kind === "service_message") return <MessageSquare className="h-4 w-4 text-primary" />;
  return <Bell className="h-4 w-4 text-muted-foreground" />;
}

export default function NotificationCenter() {
  const navigate = useNavigate();
  const { notifUnread, refresh } = useUnread();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const loadFirstPage = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications?limit=20");
      setItems(data.items || []);
      setCursor(data.next_cursor);
      setHasMore(!!data.next_cursor);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  const loadMore = async () => {
    if (!cursor || loading) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/notifications?limit=20&before=${encodeURIComponent(cursor)}`);
      setItems((cur) => [...cur, ...(data.items || [])]);
      setCursor(data.next_cursor);
      setHasMore(!!data.next_cursor);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) loadFirstPage();
  }, [open, loadFirstPage]);

  const onClickItem = async (n) => {
    // Deep link
    const data = n.data || {};
    let target = "/";
    if (n.kind === "service_message" && data.service_id) target = `/servis-kayitlari/${data.service_id}`;
    else if (n.kind === "general_message") target = "/genel-grup";
    // Tekil okundu
    if (!n.read_at) {
      try { await api.post("/notifications/mark-read", { ids: [n.id] }); } catch (e) { /* ignore */ }
      setItems((cur) => cur.map((x) => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x));
      refresh();
    }
    setOpen(false);
    navigate(target);
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-read", {});
      setItems((cur) => cur.map((x) => ({ ...x, read_at: x.read_at || new Date().toISOString() })));
      refresh();
    } catch (e) { /* ignore */ }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-center-btn" aria-label="Bildirim Merkezi">
          <Bell className="h-5 w-5" />
          {notifUnread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold leading-none"
              data-testid="notification-center-unread"
            >
              {notifUnread > 99 ? "99+" : notifUnread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notification-center-popover">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="font-display font-bold text-sm">Bildirim Merkezi</div>
          <button
            type="button"
            onClick={markAllRead}
            className="text-[11px] text-primary hover:underline inline-flex items-center gap-1"
            data-testid="mark-all-read-btn"
          >
            <CheckCheck className="h-3 w-3"/>Tümünü okundu yap
          </button>
        </div>
        <div className="max-h-[420px] overflow-y-auto divide-y divide-border" data-testid="notification-list">
          {loading && items.length === 0 && (
            <div className="p-6 text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin"/>Yükleniyor...
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="p-8 text-center text-xs text-muted-foreground flex flex-col items-center gap-2">
              <BellOff className="h-6 w-6 text-muted-foreground/60"/>
              Bildiriminiz yok.
            </div>
          )}
          {items.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => onClickItem(n)}
              className={`w-full text-left px-4 py-3 hover:bg-accent hover:text-accent-foreground transition-colors flex gap-3 ${n.read_at ? "" : "bg-primary/10"}`}
              data-testid={`notification-item-${n.id}`}
            >
              <div className="mt-0.5">
                <NotificationIcon kind={n.kind}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-semibold truncate flex-1">{n.title}</div>
                  <div className="text-[10px] text-muted-foreground whitespace-nowrap">{relativeTime(n.created_at)}</div>
                </div>
                <div className="text-xs text-muted-foreground truncate mt-0.5">{n.body}</div>
              </div>
              {!n.read_at && (
                <span className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" aria-label="Okunmadı"/>
              )}
            </button>
          ))}
        </div>
        {hasMore && (
          <div className="border-t border-border px-4 py-2">
            <button
              type="button"
              onClick={loadMore}
              disabled={loading}
              className="w-full text-xs text-primary hover:underline inline-flex items-center justify-center gap-1"
              data-testid="notification-load-more"
            >
              {loading ? <><Loader2 className="h-3 w-3 animate-spin"/>Yükleniyor…</> : <>Daha eski bildirimleri gör</>}
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
